import React, {useState} from "react"
import Papa from "papaparse"

import {StatementItem} from "@irati/types"
import FormattedMessage, {injectIntl, InjectIntl} from "@irati/utils/lang"
import StatusType from "@irati/wallet/types/enums/Status"
import perkboxLogo from "@irati/ui/images/logo-alt.svg"
import withBranding, {WithBranding} from "@irati/brand/withBranding"
import TextButton from "@irati/ui/src/Button/TextButton"
import flexiPointService from "@irati/wallet/services/flexiRewards"
import Body from "@irati/ui/src/Typography/Body"
import mainTheme from "@irati/ui/src/global/theme"
import makeCSV from "@irati/utils/csv/makeCSV"
import Loading from "@irati/ui/src/Loading"
import Spacing from "@irati/ui/src/Spacing"

import PrimaryDetails from "../PrimaryDetails"
import SecondaryDetails from "../SecondaryDetails/ViewMessage"
import ExpanderItem from "../ExpanderItem"

interface Props extends InjectIntl, WithBranding {
  statementItem: StatementItem
  className?: string
}
const BulkRewardPointsTransfer: React.FC<Props> = (props) => {
  const {statementItem, formatMessage, branding, className} = props
  const [generatingReportPromise, setGeneratingReportPromise] = useState<Promise<void> | null>(null)

  const status = StatusType.REWARD_WALLET_SPENT

  const primaryDetails = {
    image: branding.businessLogoUrl || perkboxLogo,
    title: formatMessage({id: "wallet.statementItem.bulkRewardTransfer.primaryDetails.title"}),
    subTitle: formatMessage(
      {id: "wallet.statementItem.adminReward.bulkTransfer.recipientsRewarded"},
      {recipients: statementItem.transfer?.destinationIds.length}
    ),
    amount: statementItem.transfer!.amount!,
    currency: statementItem.transfer?.currency,
    status
  }

  const generateReport = async () => {
    const generatingReportCall = new Promise<void>(async (resolve, reject) => {
      try {
        const transferOutcome = await flexiPointService.getBulkRewardTransferOutcome({
          uuid: statementItem.transactionId!
        })
        const filename = `report-bulk-transfer-${statementItem.transactionId}.csv`
        const csv = Papa.unparse({
          fields: ["firstName", "lastName", "email", "status"],
          data: transferOutcome.rewardedUsers!
        })
        makeCSV(csv, filename)
        resolve()
      } catch (e) {
        console.error("[generateReport] Error while generating the CSV:", e)
        reject(e)
      }
      setGeneratingReportPromise(null)
    })
    setGeneratingReportPromise(generatingReportCall)
  }
  const secondaryDetails = {
    userMessage: (
      <Spacing marginLeft={generatingReportPromise ? mainTheme.spacing.two : "0"}>
        <Loading load={generatingReportPromise}>
          <TextButton onClick={generateReport}>
            <Body color={mainTheme.colors.primary2} small weight="bold">
              <FormattedMessage id="wallet.statementItem.adminReward.transfer.exportCSVReport" />
            </Body>
          </TextButton>
        </Loading>
      </Spacing>
    ),
    message: statementItem.transfer?.message
  }
  return (
    <ExpanderItem
      className={className}
      PrimaryElement={() => <PrimaryDetails imgHeightAuto data={primaryDetails} />}
      SecondaryElement={<SecondaryDetails data={secondaryDetails} />}
    />
  )
}

export default injectIntl(withBranding(BulkRewardPointsTransfer))
